import {ScrapperResponse} from '@/model/Scrapper';

const SESSION_KEY_PREFIX = 'analyzeScrapeFail:';

export function scrapedOutlinePresent(response: ScrapperResponse): boolean {
    const headings = [
        ...(response.h1h || []),
        ...(response.h2h || []),
    ];
    return headings.some((heading) => String(heading || '').trim().length > 0);
}

export function scrapedBodyLength(response: ScrapperResponse): number {
    const subparts: string[] = [];
    for (let i = 1; i <= 6; ++i) {
        const row = response as ScrapperResponse & {
            [key: string]: string[] | undefined;
        };
        subparts.push(...(row['h' + i + 'h'] || []));
        subparts.push(...(row['h' + i + 'p'] || []));
    }
    return subparts.reduce((acc, cur) => acc + String(cur || '').length, 0);
}

/** Same threshold as ArticleCreation.checkScrapedResponse. */
export function scrapedResponseMeetsQualityBar(response: ScrapperResponse): boolean {
    if (!response.h1h || response.h1h.length === 0) {
        return false;
    }
    return scrapedBodyLength(response) >= 500;
}

function sectionHeadingCount(response: ScrapperResponse): number {
    let count = 0;
    for (let level = 2; level <= 6; level++) {
        const row = response as ScrapperResponse & {[key: string]: string[] | undefined};
        for (const heading of row['h' + level + 'h'] || []) {
            if (String(heading || '').trim()) {
                count += 1;
            }
        }
    }
    return count;
}

function plainFieldLength(value: string): number {
    return value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().length;
}

/** Plain characters kept in the extract, tags stripped. */
export function scrapedPlainLength(response: ScrapperResponse): number {
    let total = 0;
    for (let level = 1; level <= 6; level++) {
        const row = response as ScrapperResponse & {[key: string]: string[] | undefined};
        for (const heading of row['h' + level + 'h'] || []) {
            total += plainFieldLength(String(heading || ''));
        }
        for (const paragraph of row['h' + level + 'p'] || []) {
            total += plainFieldLength(String(paragraph || ''));
        }
    }
    return total;
}

/**
 * An outline with no section and only a small share of the page.
 * A short page that is itself one block is not weak. Missing page length
 * keeps the older partial/success split.
 */
export function isStructurallyWeakExtract(response: ScrapperResponse | null | undefined): boolean {
    if (!response || response.status !== 200 || !scrapedOutlinePresent(response)) {
        return false;
    }
    if (sectionHeadingCount(response) > 0) {
        return false;
    }
    const source = Number(response.sourceProseLength || 0);
    if (source < 1500) {
        return false;
    }
    return scrapedPlainLength(response) / source < 0.4;
}

/**
 * Anti-scrape empty extract, transport failures, and a structurally weak
 * extract use the failed-scrape dialog (refund). A non-empty outline that
 * fails the quality bar stays partial.
 */
export function isAnalyzeScrapeExtractionFailed(response: ScrapperResponse | null | undefined): boolean {
    if (!response || response.status !== 200) {
        return true;
    }
    if (!scrapedOutlinePresent(response)) {
        return true;
    }
    return isStructurallyWeakExtract(response);
}

export function isAnalyzeScrapeExtractionPartial(response: ScrapperResponse): boolean {
    if (isAnalyzeScrapeExtractionFailed(response)) {
        return false;
    }
    return !scrapedResponseMeetsQualityBar(response);
}

export function markAnalyzeScrapeFailPending(articleId: string): void {
    if (!articleId) {
        return;
    }
    try {
        sessionStorage.setItem(SESSION_KEY_PREFIX + articleId, '1');
    } catch {
        // ignore private mode / quota
    }
}

export function hasAnalyzeScrapeFailPending(articleId: string): boolean {
    if (!articleId) {
        return false;
    }
    try {
        return sessionStorage.getItem(SESSION_KEY_PREFIX + articleId) === '1';
    } catch {
        return false;
    }
}

export function clearAnalyzeScrapeFailPending(articleId: string): void {
    if (!articleId) {
        return;
    }
    try {
        sessionStorage.removeItem(SESSION_KEY_PREFIX + articleId);
    } catch {
        // ignore
    }
}
